import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Plus, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import OSLayout from "../../components/os/OSLayout";
import { api } from "../../lib/api";
import SelectField from "../../components/os/SelectField";
import { input, GrHeader, GrModal } from "./_grShared";

const ROOMS = [{ value: "sala", label: "Sala" }, { value: "recamara", label: "Recámara" }, { value: "cocina", label: "Cocina" }, { value: "bano", label: "Baño" }, { value: "comedor", label: "Comedor" }, { value: "exterior", label: "Exterior" }];
const STYLES = [{ value: "minimalista", label: "Minimalista" }, { value: "contemporaneo", label: "Contemporáneo" }, { value: "industrial", label: "Industrial" }, { value: "escandinavo", label: "Escandinavo" }, { value: "clasico", label: "Clásico" }, { value: "mediterraneo", label: "Mediterráneo" }, { value: "luxury", label: "Luxury" }, { value: "japandi", label: "Japandi" }];

function Compare({ before, after }) {
  const [pos, setPos] = useState(50);
  return (
    <div className="relative aspect-video rounded-lg overflow-hidden bg-navy/5 select-none">
      {before ? <img src={before} alt="antes" className="absolute inset-0 w-full h-full object-cover" /> : <div className="absolute inset-0 flex items-center justify-center text-xs text-navy/40">Antes</div>}
      <div className="absolute inset-0 overflow-hidden" style={{ width: `${pos}%` }}>
        {after ? <img src={after} alt="despues" className="h-full object-cover" style={{ width: `${100 / (pos / 100)}%`, maxWidth: "none" }} /> : <div className="w-full h-full flex items-center justify-center bg-violet/10 text-xs text-violet">Después</div>}
      </div>
      <div className="absolute top-0 bottom-0 w-0.5 bg-white shadow" style={{ left: `${pos}%` }} />
      <input type="range" min="0" max="100" value={pos} onChange={(e) => setPos(Number(e.target.value))} className="absolute bottom-2 left-1/2 -translate-x-1/2 w-3/4" />
      <span className="absolute top-2 left-2 badge bg-white/80 text-navy/60">Antes</span>
      <span className="absolute top-2 right-2 badge badge-violet">Después</span>
    </div>
  );
}

export default function GrStaging() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ property: "", room: "sala", style: "contemporaneo", before_url: "", after_url: "", notes: "" });

  const load = () => api.get("/gr/staging").then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!f.property.trim()) return toast.error("Propiedad requerida");
    try { await api.post("/gr/staging", f); setOpen(false); setF({ property: "", room: "sala", style: "contemporaneo", before_url: "", after_url: "", notes: "" }); load(); toast.success("Staging creado"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  const del = async (id) => { try { await api.delete(`/gr/staging/${id}`); load(); } catch { /* */ } };

  return (
    <OSLayout title="OMNIA Virtual Staging">
      <div className="space-y-6" data-testid="gr-staging-page">
        <div className="flex items-end justify-between gap-3">
          <GrHeader title="OMNIA Virtual Staging" desc="Amuebla y decora digitalmente espacios vacíos. Compara Antes y Después por estilo." />
          <button onClick={() => setOpen(true)} className="btn btn-primary shrink-0" data-testid="gr-staging-new"><Plus className="w-4 h-4" /> Nuevo staging</button>
        </div>

        {loading ? <div className="py-12 flex justify-center text-navy/40"><Loader2 className="w-6 h-6 animate-spin" /></div> :
          items.length === 0 ? <div className="card-surface py-12 text-center text-navy/40 text-sm">Sin propuestas de staging.</div> :
          <div className="grid md:grid-cols-2 gap-4" data-testid="gr-staging-grid">
            {items.map((s) => (
              <motion.div key={s.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="card-surface p-4" data-testid={`gr-staging-${s.id}`}>
                <div className="flex items-start justify-between mb-3"><div><div className="font-medium">{s.property}</div><div className="text-xs text-navy/50">{ROOMS.find((r) => r.value === s.room)?.label} · {STYLES.find((x) => x.value === s.style)?.label}</div></div><button onClick={() => del(s.id)} className="text-navy/25 hover:text-destructive"><Trash2 className="w-4 h-4" /></button></div>
                <Compare before={s.before_url} after={s.after_url} />
                {s.notes && <p className="text-xs text-navy/60 mt-2">{s.notes}</p>}
              </motion.div>
            ))}
          </div>}
      </div>

      {open && <GrModal title="Nuevo Virtual Staging" onClose={() => setOpen(false)} testid="gr-staging-dialog" wide>
        <div className="space-y-3">
          <input className={input} placeholder="Propiedad" value={f.property} onChange={(e) => setF({ ...f, property: e.target.value })} data-testid="gr-staging-property" />
          <div className="grid grid-cols-2 gap-3">
            <SelectField value={f.room} options={ROOMS} onChange={(v) => setF({ ...f, room: v })} />
            <SelectField value={f.style} options={STYLES} onChange={(v) => setF({ ...f, style: v })} />
          </div>
          <input className={input} placeholder="URL imagen ANTES (espacio vacío)" value={f.before_url} onChange={(e) => setF({ ...f, before_url: e.target.value })} />
          <input className={input} placeholder="URL imagen DESPUÉS (amueblado)" value={f.after_url} onChange={(e) => setF({ ...f, after_url: e.target.value })} />
          <textarea className={input} rows={2} placeholder="Notas" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} />
        </div>
        <div className="flex justify-end gap-2 mt-5"><button onClick={() => setOpen(false)} className="btn btn-ghost">Cancelar</button><button onClick={create} className="btn btn-primary" data-testid="gr-staging-submit">Crear</button></div>
      </GrModal>}
    </OSLayout>
  );
}
